// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useEffect } from "react"
import styled, { ThemeProvider } from "styled-components/macro"
import { GlobalStyles, theme } from "styled/styles"
import { BrowserRouter } from "react-router-dom"
import { Helmet } from "react-helmet"
import Routes from "app/Routes"
import SnackBar from "./features/snackbar/SnackBar"
import { BASE_PATH } from "./app/routing"
import { useDispatch, useSelector } from "react-redux"
import * as UserContextActions from "./features/user-context/actions"
import * as UserContextSlice from "./features/user-context/slice"
import { UserId } from "./features/user-context/slice"
import { ReactComponent as SvgSprite } from "resources/images/svg-sprite.svg"
import LoadingIconWrapper from "./features/shared/LoadingIconWrapper"
import { BehaviorSubject } from "rxjs"
import * as TimelineActions from "./features/timeline/actions"
import { isIosWiseTime, storagePrefix } from "./app/config"
import { sanitizeLocalStorage } from "./lib/localStorage"

const useTimesheetFieldsLocalStorageKey = "wt_timeline_use_timesheet_fields"
const GRID_ACTIVITY_TYPES_VIEW_TYPE = storagePrefix + "GRID_ACTIVITY_TYPES_VIEW_TYPE"

const Main = styled.main`
  display: flex;
  flex-direction: column;
`

export const SIDEBAR_STORAGE_KEY = "WT_SIDEBAR_IS_COLLAPSED"
export const SIDEBAR_COLLAPSED_CLASSNAME = "wt-sidebar--collapsed"
// On application init, assume that we have a valid access token.
// If any RPC call fails with status UNAUTHENTICATED, we will send true and request for a new access token.
export const accessTokenInFlight = new BehaviorSubject<boolean>(false)

const handleAppcues = (userId: UserId, props: { email: string, name: string}) => {
  const noTypeWindow = window as any
  if (!noTypeWindow.loadjs) {
    return
  }
  // eslint-disable-next-line lingui/no-unlocalized-strings
  noTypeWindow.loadjs.ready("Appcues", () => {
    if (!noTypeWindow.Appcues) {
      return
    }
    noTypeWindow.Appcues.start()
    noTypeWindow.Appcues.identify(userId, props)
  })
}

const App = (): React.ReactElement<unknown> => {

  /**
   * Ensure the user is logged in by:
   *
   * 1. Calling the `whoAmI` endpoint to check if the user has a valid id token
   * 2. The accompanying retry policy will attempt to exchange a _refresh token for an _id token if the previous call
   *    fails; if the token exchange fails then the user is redirected to the login page.
   */
  const dispatch = useDispatch()
  useEffect(() => {
    // handle collapsed state of wt-sidebar to avoid content jump
    const { body }: Document = document
    if (body !== null && localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true") {
      body.classList.add(SIDEBAR_COLLAPSED_CLASSNAME)
    }
    dispatch(UserContextActions.fetchUserDetails())
    dispatch(TimelineActions.syncCurrentDay())
    dispatch(TimelineActions.periodicRefreshTimesheetFieldConfigs())

    document.body.addEventListener("mousedown", function() {
      document.body.classList.add("focus-using-mouse")
    })

    document.body.addEventListener("keydown", function(event) {
      if (event.key === "Tab") {
        document.body.classList.remove("focus-using-mouse")
      }
    })
    sanitizeLocalStorage([
      useTimesheetFieldsLocalStorageKey,
      GRID_ACTIVITY_TYPES_VIEW_TYPE,
    ])
  }, [])

  const user = useSelector(UserContextSlice.selectUser)
  const { id, email, firstName, lastName } = user || {}
  useEffect(() => {
    if (id && email && firstName && lastName) {
      handleAppcues(id, { email, name: firstName + " " + lastName })
    }
  }, [id, email, firstName, lastName])
  useEffect(() => {
    if (id !== undefined) {
      dispatch(UserContextActions.listNotifications())
    }
  }, [id])
  // eslint-disable-next-line lingui/no-unlocalized-strings
  const maximumScale = isIosWiseTime ? ", maximum-scale=1" : ""

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <meta name="viewport" content={`width=device-width, initial-scale=1${maximumScale}`}/>
      </Helmet>
      <GlobalStyles/>
      <SvgSprite className="hidden"/>
      <Main>
        <BrowserRouter basename={BASE_PATH}>
          <React.Suspense fallback={<LoadingIconWrapper/>}>
            <Routes/>
          </React.Suspense>
        </BrowserRouter>
        <SnackBar/>
      </Main>
    </ThemeProvider>
  )
}

export default App
